/**
 * Document auto-extract — client helper.
 *
 * Endpoint: POST /api/extract (Cloudflare Pages Function, multipart file+kind)
 * → Gemini Vision extraction → form auto-fill.
 *
 * Two kinds:
 *  - "bizcert"  한국 사업자등록증 (거래처 / 자사정보 폼)
 *  - "product"  FDA 510(k) / 등록증 / 제품 스펙시트 (제품 폼)
 *
 * The Gemini API key lives ONLY in the server function (context.env). This
 * client never sees it.
 */

export type ExtractKind = "bizcert" | "product";

/** 거래처/자사 — 한국 사업자등록증 추출 결과. */
export interface BizCertFields {
  biz_no: string;
  company_name: string;
  ceo_name: string;
  address: string;
  biz_type: string;
  biz_category: string;
}

/** 제품 — FDA/스펙 서류 추출 결과. */
export interface ProductFields {
  model_name: string;
  description_en: string;
  device_class: string;
  fda_510k_number: string;
  fda_fei: string;
  fda_device_listing: string;
  hs_code: string;
  intended_use: string;
  net_weight_kg: number | null;
  gross_weight_kg: number | null;
}

export interface ExtractResult {
  ok: boolean;
  kind?: ExtractKind;
  /** Shape depends on `kind`: BizCertFields | ProductFields. */
  fields?: BizCertFields | ProductFields;
  error?: string;
}

/** Error code → 한국어 사용자 메시지 (SEND EXTRACT_ERR 이식 + 신규 코드). */
export const EXTRACT_ERR: Record<string, string> = {
  no_extract_engine:
    "자동추출 엔진(Gemini)이 설정되지 않았습니다. 수동으로 입력하세요.",
  engine_auth: "추출 엔진 API 키 오류입니다. 관리자에게 키 설정을 확인하세요.",
  file_required: "파일을 선택하세요.",
  file_too_large: "파일이 너무 큽니다 (최대 10MB).",
  unsupported_file: "이미지(PNG/JPG/WEBP) 또는 PDF만 업로드할 수 있습니다.",
  extract_failed: "추출에 실패했습니다. 더 선명한 이미지로 다시 시도하세요.",
  extract_incomplete:
    "이미지에서 글자를 충분히 읽지 못했습니다. 더 밝고 선명한 사진(또는 PDF)으로 다시 시도하세요.",
  parse_failed: "추출 결과를 해석하지 못했습니다. 더 선명한 이미지로 다시 시도하세요.",
  internal_error: "서버 오류가 발생했습니다.",
  network_error: "네트워크 오류입니다.",
};

export const extractErrMsg = (e?: string): string =>
  e ? (EXTRACT_ERR[e] ?? e) : "알 수 없는 오류";

/**
 * Upload a document and get auto-extracted fields.
 * Never throws — network/parse errors come back as `{ ok:false, error }`.
 */
export async function extractDocument(
  file: File,
  kind: ExtractKind,
): Promise<ExtractResult> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("kind", kind);
  try {
    const res = await fetch("/api/extract", { method: "POST", body: fd });
    return (await res.json()) as ExtractResult;
  } catch {
    return { ok: false, error: "network_error" };
  }
}
